import "~/styles/index.scss";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/700.css";

import { type ReactNode } from "react";
import {
  Links,
  Meta,
  type MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
} from "react-router";

import {
  INDEX_ROBOTS_DIRECTIVE,
  NOINDEX_ROBOTS_DIRECTIVE,
} from "~/constants/robots";
import { LicenseInit } from "~/feature/license/LicenseInit";
import { ClarityScript } from "~/scripts/ClarityScript";
import { GoogleAnalytics } from "~/scripts/GoogleAnalytics";
import { GoogleTagManager } from "~/scripts/GoogleTagManager";
import { shouldLoadAnalytics } from "~/scripts/shouldLoadAnalytics";
import { generateMetaData } from "~/utils/generateMetaData";
import { getRuntimeEnv } from "~/utils/getRuntimeEnv";

import { ErrorBoundary as RootErrorBoundary } from "./components/ErrorBoundary/ErrorBoundary";
import type { Route } from "./routes/+types/home";

export const meta: MetaFunction = (metaArgs) => {
  // Any error page that bubbles up here (404, 503, 500) is not content.
  return generateMetaData({
    metaArgs,
    robotsDirective: metaArgs.error
      ? NOINDEX_ROBOTS_DIRECTIVE
      : INDEX_ROBOTS_DIRECTIVE,
  });
};

export function Layout({ children }: { children: ReactNode }) {
  const runtimeEnv = getRuntimeEnv();
  const { pathname } = useLocation();
  const withAnalytics = shouldLoadAnalytics(pathname);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* Inject runtime environment variables for hydration */}
        <script
          dangerouslySetInnerHTML={{
            __html: `window._env_ = ${JSON.stringify(runtimeEnv)};`,
          }}
        />
        {withAnalytics && <GoogleAnalytics />}
        {withAnalytics && <ClarityScript />}
        {withAnalytics && <GoogleTagManager />}
        <LicenseInit />
        <Meta />
        <Links />
        <link
          rel="apple-touch-icon"
          sizes="57x57"
          href="/apple-icon-57x57.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="60x60"
          href="/apple-icon-60x60.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="72x72"
          href="/apple-icon-72x72.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="76x76"
          href="/apple-icon-76x76.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="114x114"
          href="/apple-icon-114x114.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="120x120"
          href="/apple-icon-120x120.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="144x144"
          href="/apple-icon-144x144.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="152x152"
          href="/apple-icon-152x152.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-icon-180x180.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="192x192"
          href="/android-icon-192x192.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="96x96"
          href="/favicon-96x96.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="manifest" href="/manifest.json" />
        <link
          rel="alternate"
          type="application/rss+xml"
          title="PredictionHero Insights"
          href="/insights/feed.xml"
        />
        <meta name="msapplication-TileColor" content="#ffffff" />
        <meta name="msapplication-TileImage" content="/ms-icon-144x144.png" />
        <meta name="theme-color" content="#ffffff" />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary(props: Route.ErrorBoundaryProps) {
  return <RootErrorBoundary {...props} />;
}
